<script setup lang="ts">
import type { Message } from '@/composables/useDemoChat';
import claudeCodeIcon from '@/assets/logos/claude-code.png';

type LlmStyle = 'chatgpt' | 'claude' | 'claude-code';

const props = withDefaults(defineProps<{
  llmStyle?: LlmStyle;
  visibleMessages: Message[];
  isTyping: boolean;
  typingContent: string;
  typingRole: 'user' | 'assistant';
}>(), {
  llmStyle: 'chatgpt',
});

const llmTitle = {
  'chatgpt': 'ChatGPT',
  'claude': 'Claude',
  'claude-code': 'Claude Code',
};
</script>

<template>
  <div>
    <h2 class="text-xl font-semibold text-foreground mb-3">
      {{ llmTitle[props.llmStyle] }}
    </h2>

    <!-- ChatGPT Style -->
    <div v-if="props.llmStyle === 'chatgpt'" class="rounded-lg border border-border bg-white dark:bg-[#212121] overflow-hidden shadow-md">
      <!-- Header bar -->
      <div class="px-3 py-2 border-b border-gray-100 dark:border-transparent flex items-center">
        <div class="flex items-center gap-1.5">
          <span class="text-sm font-medium text-gray-900 dark:text-gray-100">LLM SR</span>
          <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      <!-- Chat messages -->
      <div class="p-4 space-y-4 h-[212px] md:h-[312px] overflow-hidden flex flex-col">
        <div class="flex-1 space-y-4">
          <TransitionGroup name="message">
            <div
              v-for="(message, index) in visibleMessages"
              :key="index"
              :class="[message.skipTransition ? 'skip-transition' : '']"
            >
              <!-- User message: dark pill on right -->
              <div v-if="message.role === 'user'" class="flex justify-end">
                <div class="max-w-[85%] px-4 py-2.5 rounded-3xl text-sm bg-[#1a1a1a] dark:bg-[#303030] text-white">
                  {{ message.content }}
                </div>
              </div>
              <!-- Assistant message: plain text, no bubble -->
              <div v-else class="text-sm text-gray-900 dark:text-gray-100 leading-relaxed">
                {{ message.content }}
              </div>
            </div>
          </TransitionGroup>

          <!-- Typing content (typewriter effect for assistant) -->
          <div v-if="typingContent" class="text-sm text-gray-900 dark:text-gray-100 leading-relaxed">
            {{ typingContent }}<span class="inline-block w-0.5 h-4 ml-0.5 align-middle animate-pulse bg-gray-400"></span>
          </div>

          <!-- Typing indicator (dots) -->
          <Transition name="fade">
            <div v-if="isTyping && !typingContent">
              <div v-if="typingRole === 'user'" class="flex justify-end">
                <div class="px-4 py-2.5 rounded-3xl flex items-center gap-1 bg-[#1a1a1a] dark:bg-[#303030]">
                  <span class="w-1.5 h-1.5 rounded-full bg-white/50 animate-bounce"></span>
                  <span class="w-1.5 h-1.5 rounded-full bg-white/50 animate-bounce" style="animation-delay: 0.1s"></span>
                  <span class="w-1.5 h-1.5 rounded-full bg-white/50 animate-bounce" style="animation-delay: 0.2s"></span>
                </div>
              </div>
              <div v-else class="flex items-center gap-1">
                <span class="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce"></span>
                <span class="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style="animation-delay: 0.1s"></span>
                <span class="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style="animation-delay: 0.2s"></span>
              </div>
            </div>
          </Transition>
        </div>

        <!-- Input bar -->
        <div class="mt-auto pt-2">
          <div class="flex items-center gap-3 px-4 py-3 rounded-full border border-gray-200 dark:border-white/5 bg-gray-50 dark:bg-[#303030]">
            <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            <span class="flex-1 text-sm text-gray-400">Ask anything</span>
            <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Claude Style -->
    <div v-else-if="props.llmStyle === 'claude'" class="rounded-lg border border-border bg-[#faf9f5] dark:bg-[#262624] overflow-hidden shadow-md">
      <!-- Header bar -->
      <div class="px-3 py-2 border-b border-[#ebe7df] dark:border-transparent flex items-center justify-between">
        <div class="flex items-center gap-1.5">
          <span class="text-sm font-medium text-[#1a1915] dark:text-[#d4d1cb] truncate">Learning spaced repetition with flashcards</span>
          <svg class="w-4 h-4 text-[#8c8780] dark:text-[#8c8780]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      <!-- Chat messages -->
      <div class="p-4 space-y-4 h-[212px] md:h-[312px] overflow-hidden flex flex-col">
        <div class="flex-1 space-y-4">
          <TransitionGroup name="message">
            <div
              v-for="(message, index) in visibleMessages"
              :key="index"
              :class="[message.skipTransition ? 'skip-transition' : '']"
            >
              <!-- User message: light cream pill in light mode, dark in dark mode -->
              <div v-if="message.role === 'user'" class="flex justify-end">
                <div class="max-w-[85%] px-4 py-2.5 rounded-2xl text-sm bg-[#f1eee7] dark:bg-[#141412] text-[#1a1915] dark:text-[#e8e6e3]">
                  {{ message.content }}
                </div>
              </div>
              <!-- Assistant message: plain text, no bubble -->
              <div v-else class="text-sm text-[#1a1915] dark:text-[#e8e6e3] leading-relaxed">
                {{ message.content }}
              </div>
            </div>
          </TransitionGroup>

          <!-- Typing content (typewriter effect for assistant) -->
          <div v-if="typingContent" class="text-sm text-[#1a1915] dark:text-[#e8e6e3] leading-relaxed">
            {{ typingContent }}<span class="inline-block w-0.5 h-4 ml-0.5 align-middle animate-pulse bg-[#c96442]"></span>
          </div>

          <!-- Typing indicator (dots) -->
          <Transition name="fade">
            <div v-if="isTyping && !typingContent">
              <div v-if="typingRole === 'user'" class="flex justify-end">
                <div class="px-4 py-2.5 rounded-2xl flex items-center gap-1 bg-[#f1eee7] dark:bg-[#141412]">
                  <span class="w-1.5 h-1.5 rounded-full bg-[#1a1915]/30 dark:bg-white/50 animate-bounce"></span>
                  <span class="w-1.5 h-1.5 rounded-full bg-[#1a1915]/30 dark:bg-white/50 animate-bounce" style="animation-delay: 0.1s"></span>
                  <span class="w-1.5 h-1.5 rounded-full bg-[#1a1915]/30 dark:bg-white/50 animate-bounce" style="animation-delay: 0.2s"></span>
                </div>
              </div>
              <div v-else class="flex items-center gap-1">
                <span class="w-1.5 h-1.5 rounded-full bg-[#c96442] animate-bounce"></span>
                <span class="w-1.5 h-1.5 rounded-full bg-[#c96442] animate-bounce" style="animation-delay: 0.1s"></span>
                <span class="w-1.5 h-1.5 rounded-full bg-[#c96442] animate-bounce" style="animation-delay: 0.2s"></span>
              </div>
            </div>
          </Transition>
        </div>

        <!-- Input bar -->
        <div class="mt-auto pt-2">
          <div class="flex flex-col gap-2 px-4 py-3 rounded-2xl border border-[#e8e4db] dark:border-transparent bg-white dark:bg-[#30302e]">
            <span class="text-sm text-[#8c8780]">Reply...</span>
            <div class="flex items-center justify-between">
              <svg class="w-5 h-5 text-[#8c8780]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              <span class="text-xs text-[#8c8780]">Sonnet 4.6</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Claude Code Style (Terminal) -->
    <div v-else class="rounded-lg border border-border bg-[#f5f5f5] dark:bg-[#1a1a1a] overflow-hidden shadow-md font-mono">
      <!-- Terminal header bar -->
      <div class="px-3 py-2 border-b border-[#e0e0e0] dark:border-[#333] bg-[#e8e8e8] dark:bg-[#252525] flex items-center gap-2">
        <div class="flex gap-1.5">
          <div class="w-2.5 h-2.5 rounded-full bg-[#ff5f57]"></div>
          <div class="w-2.5 h-2.5 rounded-full bg-[#febc2e]"></div>
          <div class="w-2.5 h-2.5 rounded-full bg-[#28c840]"></div>
        </div>
        <span class="text-xs text-[#666666] dark:text-[#808080] ml-2">claude</span>
      </div>

      <!-- Terminal content -->
      <div class="p-3 space-y-2 h-[212px] md:h-[312px] overflow-hidden flex flex-col text-xs">
        <!-- Claude Code header -->
        <div class="flex gap-3 mb-1 items-start">
          <img :src="claudeCodeIcon" alt="Claude Code" class="w-8 h-8 flex-shrink-0" />
          <div class="text-[#666666] dark:text-[#808080]">
            <span class="text-[#1a1a1a] dark:text-[#c9c9c9] font-semibold">Claude Code</span> <span>v2.1.20</span><br/>
            <span>Opus 4.5 · Claude Max</span><br/>
            <span>~/dev/llm-sr</span>
          </div>
        </div>

        <div class="flex-1 space-y-2">
          <TransitionGroup name="message">
            <div
              v-for="(message, index) in visibleMessages"
              :key="index"
              :class="[message.skipTransition ? 'skip-transition' : '']"
            >
              <!-- User message: highlighted bar with prompt -->
              <div v-if="message.role === 'user'" class="flex gap-2 bg-[#e0e0e0] dark:bg-[#3a3a3a] -mx-3 px-3 py-0.5">
                <span class="text-[#16a34a] dark:text-[#50fa7b] flex-shrink-0">❯</span>
                <span class="text-[#1a1a1a] dark:text-[#e8e8e8]">{{ message.content }}</span>
              </div>
              <!-- Assistant message: with ● bullet -->
              <div v-else class="flex gap-2">
                <span class="text-[#e88a7a] flex-shrink-0">●</span>
                <span class="text-[#1a1a1a] dark:text-[#c9c9c9]">{{ message.content }}</span>
              </div>
            </div>
          </TransitionGroup>

          <!-- Typing content (typewriter effect for assistant) -->
          <div v-if="typingContent" class="flex gap-2">
            <span class="text-[#e88a7a] flex-shrink-0">●</span>
            <span class="text-[#1a1a1a] dark:text-[#c9c9c9]">{{ typingContent }}<span class="inline-block w-1.5 h-3 ml-0.5 align-middle animate-pulse bg-[#1a1a1a] dark:bg-[#c9c9c9]"></span></span>
          </div>

          <!-- Typing indicator -->
          <Transition name="fade">
            <div v-if="isTyping && !typingContent">
              <div v-if="typingRole === 'user'" class="flex gap-2 bg-[#e0e0e0] dark:bg-[#3a3a3a] -mx-3 px-3 py-0.5">
                <span class="text-[#16a34a] dark:text-[#50fa7b] flex-shrink-0">❯</span>
                <span class="text-[#666666] dark:text-[#808080] animate-pulse">...</span>
              </div>
              <div v-else class="flex gap-2">
                <span class="text-[#e88a7a] flex-shrink-0">●</span>
                <span class="text-[#666666] dark:text-[#808080] animate-pulse">thinking...</span>
              </div>
            </div>
          </Transition>
        </div>

        <!-- Input prompt -->
        <div class="mt-auto pt-2 border-t border-[#e0e0e0] dark:border-[#333] text-[#666666] dark:text-[#808080]">
          <div class="flex items-center gap-2">
            <span class="text-[#16a34a] dark:text-[#50fa7b]">❯</span>
            <span class="w-2 h-4 bg-[#1a1a1a] dark:bg-[#c9c9c9] animate-pulse"></span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Message fade-in (skip for typed messages) */
.message-enter-active:not(.skip-transition) {
  transition: opacity 0.3s ease;
}
.message-enter-from:not(.skip-transition) {
  opacity: 0;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
